import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const Privacybeleid = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacybeleid - Compleet Verzekerd</title>
        <meta name="description" content="Privacybeleid van Compleet Verzekerd. Lees hoe wij omgaan met uw persoonlijke gegevens en welke rechten u heeft onder de AVG." />
        <meta name="keywords" content="privacybeleid, avg, persoonlijke gegevens, privacy compleet verzekerd" />
        <link rel="canonical" href="https://compleetverzekerd.nl/privacybeleid/" />
      </Helmet>
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-primary mb-4">Privacybeleid</h1>
            <p className="text-xl text-muted-foreground">
              Hoe wij omgaan met uw persoonlijke gegevens
            </p>
          </div>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Inleiding</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Compleet Verzekerd hecht grote waarde aan de bescherming van uw privacy en de veiligheid 
                  van uw persoonlijke gegevens. In dit privacybeleid leggen wij uit welke persoonlijke 
                  gegevens wij van u verzamelen, hoe wij deze gebruiken en welke rechten u heeft.
                </p>
                <p className="text-muted-foreground">
                  Dit privacybeleid is van toepassing op alle diensten van Compleet Verzekerd en 
                  het gebruik van onze website.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Contactgegevens</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-medium">Compleet Verzekerd</p>
                  <p className="text-muted-foreground">Adres: Van Eesterenplein 95, 3315 KT Dordrecht</p>
                  <p className="text-muted-foreground">Telefoon: <a href="tel:0782200021" className="hover:text-primary transition-colors">078 - 220 00 21</a></p>
                  <p className="text-muted-foreground">E-mail: info@compleetverzekerd.nl</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Welke gegevens verzamelen wij?</CardTitle>
                <CardDescription>
                  Overzicht van de persoonlijke gegevens die wij verwerken
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Contactgegevens</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>Voor- en achternaam</li>
                      <li>E-mailadres</li>
                      <li>Telefoonnummer</li>
                      <li>Adresgegevens</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium mb-2">Verzekeringsgegevens</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>Huidige verzekeringen</li>
                      <li>Gewenste dekking</li>
                      <li>Risicoprofiel</li>
                      <li>Financiële situatie (voor advies)</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium mb-2">Technische gegevens</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>IP-adres</li>
                      <li>Browser type en versie</li>
                      <li>Gebruikte apparaat</li>
                      <li>Pagina bezoeken (via cookies)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Waarvoor gebruiken wij uw gegevens?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Dienstverlening</h4>
                    <p className="text-sm text-muted-foreground">
                      Het verstrekken van verzekeringsadvies, het afsluiten en beheren van verzekeringen, 
                      en het afhandelen van schades.
                    </p>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Communicatie</h4>
                    <p className="text-sm text-muted-foreground">
                      Het beantwoorden van vragen, het versturen van belangrijke informatie over 
                      uw verzekeringen en het maken van afspraken.
                    </p>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Wettelijke verplichtingen</h4>
                    <p className="text-sm text-muted-foreground">
                      Het naleven van wettelijke verplichtingen zoals de Wet op het financieel toezicht (Wft) 
                      en regelgeving omtrent witwassen en terrorismefinanciering.
                    </p>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Website verbetering</h4>
                    <p className="text-sm text-muted-foreground">
                      Het analyseren van websitegebruik om onze diensten te verbeteren 
                      (alleen met uw toestemming).
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Beveiliging van uw gegevens</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Wij nemen de beveiliging van uw persoonlijke gegevens serieus. We hebben 
                  passende technische en organisatorische maatregelen getroffen om uw gegevens 
                  te beschermen tegen verlies, misbruik, ongeoorloofde toegang, openbaarmaking, 
                  wijziging of vernietiging.
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>SSL-versleuteling voor gegevensoverdracht</li>
                  <li>Beveiligde opslag van documenten</li>
                  <li>Beperkte toegang tot persoonlijke gegevens</li>
                  <li>Regelmatige beveiligingsupdates</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Uw rechten</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium">Inzage</h4>
                    <p className="text-sm text-muted-foreground">
                      U heeft het recht om te weten welke persoonlijke gegevens wij van u hebben.
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium">Rectificatie</h4>
                    <p className="text-sm text-muted-foreground">
                      U kunt verzoeken om onjuiste gegevens te laten corrigeren.
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium">Verwijdering</h4>
                    <p className="text-sm text-muted-foreground">
                      U kunt verzoeken om uw gegevens te laten verwijderen, tenzij wij wettelijk 
                      verplicht zijn deze te bewaren.
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium">Beperking</h4>
                    <p className="text-sm text-muted-foreground">
                      U kunt verzoeken om de verwerking van uw gegevens te beperken.
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="font-medium">Overdraagbaarheid</h4>
                    <p className="text-sm text-muted-foreground">
                      U kunt verzoeken om uw gegevens in een gestructureerd formaat te ontvangen.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Contact en klachten</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Heeft u vragen over dit privacybeleid of wilt u gebruik maken van uw rechten? 
                  Neem dan contact met ons op via:
                </p>
                <div className="space-y-2 mb-4">
                  <p>E-mail: info@compleetverzekerd.nl</p>
                  <p>Telefoon: <a href="tel:0782200021" className="hover:text-primary transition-colors">078 - 220 00 21</a></p>
                </div>
                <p className="text-muted-foreground text-sm">
                  Bent u niet tevreden over hoe wij omgaan met uw persoonsgegevens? Dan kunt u 
                  een klacht indienen bij de Autoriteit Persoonsgegevens via www.autoriteitpersoonsgegevens.nl
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Wijzigingen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Wij kunnen dit privacybeleid van tijd tot tijd wijzigen. De meest actuele versie 
                  vindt u altijd op onze website. Belangrijke wijzigingen zullen wij u per e-mail 
                  of via onze website melden.
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Privacybeleid;